// ./src/components/features/Logbook/LogbookEditDrawer/sections/RefuelFieldsSection.tsx

import React, { useMemo } from "react";
import {
  NumberInput,
  Grid,
  Group,
  Text,
  Box,
  SegmentedControl,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { FormData, TANK_TYPE_MAIN, TANK_TYPE_ADDL, PriceCalcMode } from "../../types";

import { CurrencySelector } from "@/components/generic/CurrencySelector";
import { VolumeUnitSelector } from "@/components/generic/VolumeUnitSelector";
import { FuelGradeSelector } from "@/components/generic/FuelGradeSelector";

// ============================================
// Types
// ============================================

export interface RefuelFieldsSectionProps {
  formData: FormData;
  onFieldChange: (field: string | undefined, value: string | number | boolean | string[] | null) => void;
  onTankTypeChange: (value: string) => void;
  onFuelGradeChange: (name: string | undefined, value: string | null) => void;
  onVolumeUnitChange: (name: string | undefined, value: string | null) => void;
  onVolumeChange: (value: number | string) => void;
  onTotalPriceChange: (value: number | string) => void;
  onPricePerVolumeChange: (value: number | string) => void;
  onTotalPriceInHcChange: (value: number | string) => void;
  priceCalcMode: PriceCalcMode;
  onPriceCalcModeChange: (value: PriceCalcMode) => void;
  hasAdditionalTank: boolean;
  isDifferentCurrency: boolean;
  apiFieldErrors: Record<string, string | undefined>;
}

// ============================================
// Component
// ============================================

const RefuelFieldsSectionComponent: React.FC<RefuelFieldsSectionProps> = ({
  formData,
  onFieldChange,
  onTankTypeChange,
  onFuelGradeChange,
  onVolumeUnitChange,
  onVolumeChange,
  onTotalPriceChange,
  onPricePerVolumeChange,
  onTotalPriceInHcChange,
  priceCalcMode,
  onPriceCalcModeChange,
  hasAdditionalTank,
  isDifferentCurrency,
  apiFieldErrors,
}) => {
  const { t } = useTranslation(["refuels", "logbook", "common"]);

  // Tank type options
  const tankTypeOptions = useMemo(
    () => [
      { value: TANK_TYPE_MAIN, label: t("refuels:form.fields.tankType.main", { defaultValue: "Main" }) },
      { value: TANK_TYPE_ADDL, label: t("refuels:form.fields.tankType.additional", { defaultValue: "Additional" }) },
    ],
    [t]
  );

  // Price calc mode options
  const priceCalcModeOptions = useMemo(
    () => [
      { value: "total-first", label: t("refuels:form.calcMode.totalFirst") },
      { value: "unit-first", label: t("refuels:form.calcMode.unitFirst") },
    ],
    [t]
  );

  return (
    <>
      {/* Tank Type Selector (only if car has additional tank) */}
      {hasAdditionalTank && (
        <Box>
          <Text size="sm" fw={500} mb={4}>
            {t("refuels:form.fields.tankType.label", { defaultValue: "Tank" })}
          </Text>
          <SegmentedControl
            value={formData.tankType}
            onChange={onTankTypeChange}
            data={tankTypeOptions}
            size="sm"
            fullWidth
          />
        </Box>
      )}

      {/* Fuel Volume + Unit & Fuel Grade */}
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <Text size="sm" fw={500} mb={4}>
              {t("refuels:form.fields.refuelVolume.label")}{" "}
              <Text span c="red">*</Text>
            </Text>
            <Group gap="xs" align="flex-start">
              <NumberInput
                placeholder={t("refuels:form.fields.refuelVolume.placeholder")}
                value={formData.refuelVolume ?? ""}
                onChange={onVolumeChange}
                error={apiFieldErrors.refuelVolume}
                min={0}
                max={9999}
                decimalScale={3}
                size="md"
                hideControls
                style={{ flex: 1 }}
              />
              <VolumeUnitSelector
                name="volumeEnteredIn"
                value={formData.volumeEnteredIn}
                onChange={onVolumeUnitChange}
                compact
                compactWidth={100}
                size="md"
              />
            </Group>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <FuelGradeSelector
            name="fuelGrade"
            value={formData.fuelGrade}
            onChange={onFuelGradeChange}
            error={apiFieldErrors.fuelGrade}
            size="md"
            required
          />
        </Grid.Col>
      </Grid>

      {/* Price calculation mode */}
      <Box>
        <Text size="xs" c="dimmed" mb={4}>
          {t("refuels:form.calcMode.label", { defaultValue: "Price calculation" })}
        </Text>
        <SegmentedControl
          value={priceCalcMode}
          onChange={(v) => onPriceCalcModeChange(v as PriceCalcMode)}
          data={priceCalcModeOptions}
          size="xs"
          fullWidth
        />
      </Box>

      {/* Total Price & Price per unit */}
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <Text size="sm" fw={500} mb={4}>
              {t("refuels:form.fields.totalPrice.label")}{" "}
              <Text span c="red">*</Text>
            </Text>
            <Group gap="xs" align="flex-start">
              <NumberInput
                placeholder={t("refuels:form.fields.totalPrice.placeholder")}
                value={formData.totalPrice ?? ""}
                onChange={onTotalPriceChange}
                error={apiFieldErrors.totalPrice}
                min={0}
                decimalScale={2}
                size="md"
                hideControls
                style={{ flex: 1 }}
                styles={
                  priceCalcMode === "unit-first"
                    ? { input: { backgroundColor: "var(--mantine-color-gray-1)" } }
                    : undefined
                }
              />
              <CurrencySelector
                name="paidInCurrency"
                value={formData.paidInCurrency}
                onChange={onFieldChange}
                compact
                size="md"
              />
            </Group>
            {isDifferentCurrency && formData.totalPriceInHc != null && (
              <Text size="xs" c="dimmed" mt={4}>
                ≈ {formData.totalPriceInHc.toFixed(2)} {formData.homeCurrency}
              </Text>
            )}
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label={`${t("refuels:form.fields.pricePerVolume.label")} (/${formData.volumeEnteredIn})`}
            placeholder={t("refuels:form.fields.pricePerVolume.placeholder")}
            value={formData.pricePerVolume ?? ""}
            onChange={onPricePerVolumeChange}
            error={apiFieldErrors.pricePerVolume}
            min={0}
            decimalScale={3}
            size="md"
            hideControls
            styles={
              priceCalcMode === "total-first"
                ? { input: { backgroundColor: "var(--mantine-color-gray-1)" } }
                : undefined
            }
          />
        </Grid.Col>
      </Grid>

      {/* Home currency conversion */}
      {isDifferentCurrency && (
        <NumberInput
          label={t("refuels:form.fields.totalPriceInHc.label")}
          placeholder={t("refuels:form.fields.totalPriceInHc.placeholder")}
          description={`${t("refuels:form.fields.homeCurrency.label", { defaultValue: "Home currency" })}: ${formData.homeCurrency}`}
          value={formData.totalPriceInHc ?? ""}
          onChange={onTotalPriceInHcChange}
          min={0}
          decimalScale={2}
          size="md"
          hideControls
        />
      )}
    </>
  );
};

export const RefuelFieldsSection = React.memo(RefuelFieldsSectionComponent);